import { z } from 'zod'
import { bzOptional, Guid } from '../domain/common-schemas'
import { guidSchema, isoDateStringSchema, JsonString } from './_common'

export const SendDelayedSmsToTechnicianIfAssignmentUnchangedTaskName =
  'SendDelayedSmsToTechnicianIfAssignmentUnchangedTask' as const

export const SendDelayedSmsToTechnicianIfAssignmentUnchangedTaskDataSchema = z.object({
  assignmentGuid: guidSchema,
  companyGuid: guidSchema,
  comparisonJson: z.string(),
})

export type SendDelayedSmsToTechnicianIfAssignmentUnchangedTaskData = {
  assignmentGuid: Guid
  companyGuid: Guid
  comparisonJson: JsonString
}

export type SendDelayedSmsToTechnicianIfAssignmentUnchangedTask =
  SendDelayedSmsToTechnicianIfAssignmentUnchangedTaskData & {
    readonly taskType: typeof SendDelayedSmsToTechnicianIfAssignmentUnchangedTaskName
    readonly taskVersion: 1
    readonly taskGuid: Guid
    readonly delayDurationMs: number
  }

export const ReconcileExternalPayoutTaskName = 'ReconcileExternalPayoutTask' as const

export const ReconcileExternalPayoutTaskDataSchema = z.object({
  taskVersion: z.literal(1),
  externalPayoutId: z.string(),
  merchantId: z.string(),
})

export type ReconcileExternalPayoutTaskData = z.infer<typeof ReconcileExternalPayoutTaskDataSchema>

/**
 * This task is triggered by a Payout Webhook from an external payment processor (likely Tilled)
 */
export type ReconcileExternalPayoutTask = {
  readonly taskType: typeof ReconcileExternalPayoutTaskName
  readonly taskGuid: Guid
  readonly taskData: ReconcileExternalPayoutTaskData
}

export const SendPaymentReceiptTaskName = 'SendPaymentReceiptTask' as const

export const SendPaymentReceiptTaskDataSchema = z.object({
  taskVersion: z.literal(1),
  companyGuid: guidSchema,
  paymentRecordGuid: guidSchema,
})

export type SendPaymentReceiptTaskData = z.infer<typeof SendPaymentReceiptTaskDataSchema>

export type SendPaymentReceiptTask = {
  readonly taskType: typeof SendPaymentReceiptTaskName
  readonly taskGuid: Guid
  readonly taskData: SendPaymentReceiptTaskData
}

// Sync QBO Invoice Task
export const SyncQboInvoiceTaskName = 'SyncQboInvoiceTask' as const

export const SyncQboInvoiceTaskDataSchema = z.object({
  companyGuid: guidSchema,
  invoiceGuid: z.string(),
  userGuid: bzOptional(guidSchema),
  force: bzOptional(z.boolean()),
})

export type SyncQboInvoiceTaskData = z.infer<typeof SyncQboInvoiceTaskDataSchema>

export type SyncQboInvoiceTask = {
  readonly taskType: typeof SyncQboInvoiceTaskName
  readonly taskVersion: 1
  readonly taskGuid: Guid
  readonly taskData: SyncQboInvoiceTaskData
}

// Sync Accounting Invoice Task
export const SyncAccountingInvoiceTaskName = 'SyncAccountingInvoiceTask' as const

export const SyncAccountingInvoiceTaskDataSchema = z.object({
  companyGuid: guidSchema,
  invoiceGuid: z.string(),
  userGuid: bzOptional(guidSchema),
  force: bzOptional(z.boolean()),
})

export type SyncAccountingInvoiceTaskData = z.infer<typeof SyncAccountingInvoiceTaskDataSchema>

export type SyncAccountingInvoiceTask = {
  readonly taskType: typeof SyncAccountingInvoiceTaskName
  readonly taskVersion: 1
  readonly taskGuid: Guid
  readonly taskData: SyncAccountingInvoiceTaskData
}

export const SearchIndexCompanyResyncTaskName = 'SearchIndexCompanyResyncTask' as const

export const SearchIndexCompanyResyncTaskDataSchema = z.object({
  companyGuid: guidSchema,
})

export type SearchIndexCompanyResyncTaskData = z.infer<typeof SearchIndexCompanyResyncTaskDataSchema>

export type SearchIndexCompanyResyncTask = {
  readonly taskType: typeof SearchIndexCompanyResyncTaskName
  readonly taskVersion: 1
  readonly taskGuid: Guid
  readonly taskData: SearchIndexCompanyResyncTaskData
}

export const MaintenancePlanVisitAlignmentRepairTaskName = 'MaintenancePlanVisitAlignmentRepairTask' as const

export const MaintenancePlanVisitAlignmentRepairTaskDataSchema = z.object({
  companyGuid: guidSchema,
  maintenancePlanGuid: guidSchema,
  eventType: z.string(),
  occurredAt: isoDateStringSchema,
  scheduledActivationAt: bzOptional(isoDateStringSchema),
})

export type MaintenancePlanVisitAlignmentRepairTaskData = z.infer<
  typeof MaintenancePlanVisitAlignmentRepairTaskDataSchema
>

export type MaintenancePlanVisitAlignmentRepairTask = {
  readonly taskType: typeof MaintenancePlanVisitAlignmentRepairTaskName
  readonly taskVersion: 1
  readonly taskGuid: Guid
  readonly taskData: MaintenancePlanVisitAlignmentRepairTaskData
}
